import { localStorageHandler } from 'core/services';
import { action, observable, makeObservable } from 'mobx';
import { FilterParams } from 'core/models';
import _ from 'lodash';

const sortDirection = {
	asc: 'asc',
	desc: 'desc',
};

class QueryUtility {
	@observable filter;
	@observable recordCount;
	@observable totalPages;
	fetchFunc = null;
	pageableObject;

	constructor(rootStore, options = {}, customSearch) {
		makeObservable(this);
		this.rootStore = rootStore;
		this.customSearch = customSearch;
		this.filter = options.filter ? options.filter : new FilterParams(options.pageSize);
		this.recordCount = 0;
		this.totalPages = 0;
		this._init(options);
		this.pageableObject = {
			type: 'numeric',
			pageSizes: [10, 25, 50, 100],
			pageSize: this.filter.pageSize,
		};
	}

	_init(options) {
		if (options.onChangeOrder) {
			this.onChangeOrder = options.onChangeOrder;
		}
		if (options.onChangePage) {
			this.onChangePage = options.onChangePage;
		}
		if (options.onChangePageSize) {
			this.onChangePageSize = options.onChangePageSize;
		}
		if (options.embed) {
			this.filter.embed = options.embed;
		}
		if (options.fields) {
			this.filter.fields = options.fields;
		}
		if (options.ignore) {
			this.ignore = options.ignore;
		}
	}

	@action async initialize(fetchFunc, updateUrlParams = true, isModalListView = false) {
		if (this.fetchFunc == null) {
			this.fetchFunc = fetchFunc;
		}
		if (!isModalListView) {
			this.updateFilter();
		}
		return this._reloadCollection(updateUrlParams);
	}

	@action.bound
	setFilterParams = (viewStore, filter = null, tableFilter = null) => {
		if (filter != null) {
			var keys = Object.keys(filter);
			_.forEach(keys, key => {
				this.filter[key] = filter[key];
			});
		}
		if (!this.filter.orderBy && viewStore.tableData.sortObject.length > 0) {
			this.filter.orderBy = viewStore.tableData.sortObject[0]['field'];
			this.filter.orderDirection = viewStore.tableData.sortObject[0]['dir'];
		}
		if (tableFilter != null && tableFilter.visible) {
			tableFilter.visible = false;
		}
		if (viewStore.tableData != null && viewStore.tableData.columns != null) {
			let selectedColumns = _.filter(viewStore.tableData.columns, column => column.selected);
			this.filter.searchFields = _.map(selectedColumns, column => column.key);
		}
	};

	fetch(updateUrlParams = true) {
		this.changePage(1, updateUrlParams);
	}

	@action.bound
	changePage = (page, updateUrlParams = true) => {
		this.filter.pageNumber = page;
		if (this.onChangePage) {
			this.onChangePage(page);
		}
		return this._reloadCollection(updateUrlParams);
	};

	@action.bound
	changePageSize = (pageSize, updateUrlParams = true) => {
		this.filter.pageSize = pageSize;
		this.filter.pageNumber = 1;
		if (this.onChangePageSize) {
			this.onChangePageSize(pageSize);
		}
		return this._reloadCollection(updateUrlParams);
	};

	@action.bound
	changeOrder = (column, updateUrlParams = true) => {
		let sort = column.key;
		let direction = sortDirection.asc;
		if (this.filter.orderBy === sort && this.filter.orderDirection === sortDirection.asc) {
			direction = sortDirection.desc;
		}
		if (this.filter.orderBy === sort && this.filter.orderDirection === sortDirection.desc) {
			sort = null;
			direction = null;
		}
		this.filter.orderBy = sort;
		this.filter.orderDirection = direction;
		if (this.onChangeOrder) {
			this.onChangeOrder({ sort, direction });
		}
		return this._reloadCollection(updateUrlParams);
	};

	@action.bound
	setFilterPersistance = (viewStore, filterPersistanceKey, columnPersistanceKey) => {
		// Filter persistance (save)
		localStorageHandler.set(filterPersistanceKey, this.filter);
		localStorageHandler.set(columnPersistanceKey, viewStore.tableData.columns);
	};

	@action.bound
	handleResponse = (response, updateUrlParams = false) => {
		this.filter.pageNumber = response.page;
		this.filter.pageSize = response.recordsPerPage;
		this.recordCount = response.totalRecords;
		this.totalPages = Math.ceil(this.recordCount / this.filter.pageSize);
		if (response.sort) {
			const params = response.sort.split('|');
			this.filter.orderBy = params[0];
			this.filter.orderDirection = params[1];
		}
		if (updateUrlParams) {
			this.updateUrlParams();
		}
	};

	getFilterUrlParams = (filter = this.filter) => {
		const { pageNumber, pageSize, search, orderBy, orderDirection } = filter;
		let sortSlug;
		if (orderBy) {
			sortSlug = orderBy + '|' + orderDirection;
		}
		return {
			page: pageNumber,
			rpp: pageSize,
			orderBy: sortSlug,
			search: search || undefined,
		};
	};

	updateUrlParams = () => {
		if (!this.ignore) {
			const { routerStore } = this.rootStore;
			const queryParams = this.getFilterUrlParams();
			routerStore.updateQueryParams(queryParams);
		}
	};

	updateFilter = (useDefault = false) => {
		if (useDefault) {
			this.filter.reset();
			return;
		}
		const { page, rpp, orderBy, search } = this.rootStore.routerStore.routerState.queryParams;
		this.filter.pageNumber = parseInt(page) || this.filter.pageNumber;
		this.filter.pageSize = parseInt(rpp) || this.filter.pageSize;
		if (orderBy) {
			const params = orderBy.split('|');
			this.filter.orderBy = params[0];
			if (params.length > 1) {
				this.filter.orderDirection = params[1];
			}
		}
		if (search) {
			this.filter.search = search;
		}
		this.updateUrlParams();
	};

	@action.bound
	handlePageChangeEvt = evt => {
		if (evt.page.take !== this.filter.pageSize) {
			this.changePageSize(evt.page.take);
		} else {
			this.changePage(evt.page.skip / evt.page.take + 1);
		}
	};

	@action.bound resetFilter() {
		this.filter.reset();
	}

	_reloadCollection(updateUrlParams = true) {
		if (updateUrlParams) {
			this.updateUrlParams();
		}
		return this.fetchFunc(this.filter, { updateUrlParams });
	}
}

export default QueryUtility;



// WEBPACK FOOTER //
// ./src/core/utils/QueryUtility.js